import { useCallback, useEffect, useState } from "react";

import { useNotifications } from "@/contexts/NotificationsContext";

import { useApi } from "../useApi";

// --- Types ---

export interface EvalCase {
  case_id: string;
  tags: string[];
  turns: string[];
  expected_sql: string[];
  expected_answer: string;
  difficulty: "simple" | "moderate" | "complex";
  disabled: boolean;
  out_of_scope: boolean;
  allow_non_deterministic: boolean;
  score_all_turns: boolean;
  known_flaky: boolean;
  source_tab: string | null;
  lastModifiedBy: string | null;
  lastModifiedAt: string | null;
}

export interface EvalCaseFormData {
  tags: string[];
  turns: string[];
  expected_sql: string[];
  expected_answer: string;
  difficulty: "simple" | "moderate" | "complex";
  disabled: boolean;
  out_of_scope: boolean;
  allow_non_deterministic: boolean;
  score_all_turns: boolean;
  known_flaky: boolean;
  source_tab: string | null;
}

interface GetResponse {
  cases: EvalCase[];
  count: number;
}

interface UpsertResponse {
  case: EvalCase;
}

interface DeleteResponse {
  deleted: string;
}

// --- Hook ---

/**
 * Data access for the admin "Eval Dataset" page.
 *
 * Provides CRUD operations for evaluation cases:
 * - ``loadCases``: fetch all cases from GET /admin/eval-dataset
 * - ``upsertCase``: create or update via PUT /admin/eval-dataset/{case_id}
 * - ``deleteCase``: remove via DELETE /admin/eval-dataset/{case_id}
 */
const useAdminEvalDataset = () => {
  const [cases, setCases] = useState<EvalCase[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const { notifyError, notifySuccess } = useNotifications();
  const { apiGet, apiPut, apiDelete, apiPost } = useApi({ isAdmin: true });

  const loadCases = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiGet<GetResponse>("/admin/eval-dataset");
      setCases(response?.cases ?? []);
    } catch (error: any) {
      notifyError(
        "Failed to load dataset",
        error?.message || "Could not load evaluation cases."
      );
    } finally {
      setLoading(false);
    }
  }, [apiGet, notifyError]);

  const seedFromBaseline = useCallback(async (): Promise<boolean> => {
    setSeeding(true);
    try {
      const response = await apiPost<{ seeded: number }>("/admin/eval-dataset/seed", {});
      notifySuccess(
        `Loaded ${response?.seeded ?? 0} cases from baseline.`
      );
      await loadCases();
      return true;
    } catch (error: any) {
      notifyError(
        "Seed failed",
        error?.message || "Could not seed dataset from baseline."
      );
      return false;
    } finally {
      setSeeding(false);
    }
  }, [apiPost, notifyError, notifySuccess, loadCases]);

  const upsertCase = useCallback(
    async (caseId: string, data: EvalCaseFormData): Promise<boolean> => {
      setSaving(true);
      try {
        await apiPut<UpsertResponse>(
          `/admin/eval-dataset/${encodeURIComponent(caseId)}`,
          data
        );
        notifySuccess(`Evaluation case "${caseId}" saved.`);
        await loadCases();
        return true;
      } catch (error: any) {
        notifyError(
          "Save failed",
          error?.message || `Could not save case "${caseId}".`
        );
        return false;
      } finally {
        setSaving(false);
      }
    },
    [apiPut, notifyError, notifySuccess, loadCases]
  );

  const deleteCase = useCallback(
    async (caseId: string): Promise<boolean> => {
      try {
        await apiDelete<DeleteResponse>(
          `/admin/eval-dataset/${encodeURIComponent(caseId)}`
        );
        notifySuccess(`Evaluation case "${caseId}" deleted.`);
        await loadCases();
        return true;
      } catch (error: any) {
        notifyError(
          "Delete failed",
          error?.message || `Could not delete case "${caseId}".`
        );
        return false;
      }
    },
    [apiDelete, notifyError, notifySuccess, loadCases]
  );

  useEffect(() => {
    loadCases();
  }, [loadCases]);

  return {
    cases,
    loading,
    saving,
    seeding,
    loadCases,
    upsertCase,
    deleteCase,
    seedFromBaseline,
  };
};

export default useAdminEvalDataset;
